import { createSelector } from '@reduxjs/toolkit'
import type { Route, Stop, DriverOption, DistributionZone } from '../../../../pages/Dispatch/optimizer/lib/types'
import { orderRoutesForBoard } from '../../../../pages/Dispatch/optimizer/lib/normalizeRoutes'
import { isPseudoRoute } from '../../../../pages/Dispatch/optimizer/lib/types'
import { isHiddenBucket } from '../../../../pages/Dispatch/optimizer/lib/hiddenBuckets'

const selectOptimizer = (s: any) => s.optimizer
const selectRoutes = (s: any): Route[] => s.optimizer.routes
const selectRouteVisibility = (s: any): Record<string, boolean> => s.optimizer.routeVisibility

/**
 * Reorder non-pseudo routes per routeOrder (session-only column drag order).
 * Listed vehicles come first in given order; unlisted reals appended in original order.
 * Pseudo routes stay pinned at their existing positions in `ordered`.
 */
export const applyRouteOrder = (ordered: Route[], routeOrder: string[]): Route[] => {
  if (!routeOrder || routeOrder.length === 0) return ordered
  const rank = new Map(routeOrder.map((v, i) => [v, i]))
  // Reorder ALL columns (including the PreRoute / ReadyToRoute / Unfit pseudo
  // buckets) by routeOrder; columns not in routeOrder keep their original relative
  // order (stable sort) after the listed ones. Default (empty routeOrder) keeps
  // orderRoutesForBoard's ordering.
  return [...ordered].sort((a, b) =>
    (rank.has(a.vehicle) ? (rank.get(a.vehicle) as number) : Number.MAX_SAFE_INTEGER) -
    (rank.has(b.vehicle) ? (rank.get(b.vehicle) as number) : Number.MAX_SAFE_INTEGER))
}

export const selectBoardRoutes = createSelector(
  [selectRoutes, (s: any) => s.optimizer.routeOrder ?? []],
  // Hidden buckets (PreRoute on MBE) are dropped from the rendered board/map.
  // reorderColumns applies the same filter so column-drag indices stay aligned.
  (routes, order) => applyRouteOrder(orderRoutesForBoard(routes), order).filter((r) => !isHiddenBucket(r.vehicle)),
)

// Stops that are rendered as selectable order dots on the optimizer map and are
// eligible for draw/lasso bulk-assign. This must mirror both what OptimizerMap
// draws (all visible routes — INCLUDING the pseudo PreRoute/ReadyToRoute/Unfit
// buckets — minus the shared HUB depot markers) and what assignStopsBulk moves
// (any non-HUB stop). Excluding pseudo buckets or counting hubs makes the
// draw-assign "N stops selected" count disagree with reality.
export const selectVisibleStops = createSelector(
  [selectRoutes, selectRouteVisibility],
  (routes, vis): Stop[] => {
    const out: Stop[] = []
    for (const r of routes) {
      if (vis[r.vehicle] === false) continue   // default-visible unless explicitly false
      if (isHiddenBucket(r.vehicle)) continue   // PreRoute (MBE) draws no markers / isn't lasso-selectable
      for (const s of r.stops) {
        if (s.orderType === 'HUB') continue    // depot badge, not a selectable order
        out.push(s)
      }
    }
    return out
  },
)

export interface TimelineMetric { vehicle: string; stops: number; km: number; hours: number; optimized: boolean }
// Backend totals are km (number) + a preformatted time string ("12h 46m"); a
// legacy number is seconds. Coerce to hours for the timeline.
const totalTimeToHours = (t: number | string | undefined): number => {
  if (typeof t === 'number') return Math.round((t / 3600) * 10) / 10
  if (typeof t === 'string') {
    const h = Number(/(\d+)\s*h/.exec(t)?.[1] ?? 0)
    const m = Number(/(\d+)\s*m/.exec(t)?.[1] ?? 0)
    return Math.round((h + m / 60) * 10) / 10
  }
  return 0
}
export const selectTimelineMetrics = createSelector([selectRoutes], (routes): TimelineMetric[] =>
  routes
    .filter((r) => !isPseudoRoute(r.vehicle))
    .map((r) => ({
      vehicle: r.vehicle,
      stops: r.stops.filter((s: any) => s.orderType !== 'HUB').length,
      km: Math.round((r.totalDistance ?? 0) * 10) / 10,
      hours: totalTimeToHours(r.totalTime),
      optimized: !!r.optimized,
    })),
)

export const selectOptimizerLoading = createSelector([selectOptimizer], (o) => o.loading)

// ─── Region-filtered driver selectors ────────────────────────────────────────
//
// `dispatchData.drivers` holds ALL drivers (loaded once on app start from USERS
// where isDriver). We filter to only the drivers whose distributionZones overlap
// the currently selected region, resolving zone→region via
// `optimizer.distributionZones`. Drivers with isAllDistributionZones===true are
// always included.
//
// The result is in the optimizer DriverOption shape so RouteColumn / DrawAssignPanel
// can use it directly (same structure as the order-derived s.optimizer.drivers they
// previously used for OPTIONS).

const EMPTY_DISPATCH_DRIVERS: any[] = []
const EMPTY_ZONES: DistributionZone[] = []

const selectAllDispatchDrivers = (s: any): any[] =>
  s.dispatchData?.drivers ?? EMPTY_DISPATCH_DRIVERS

const selectOptimizerDistributionZones = (s: any): DistributionZone[] =>
  s.optimizer?.distributionZones ?? EMPTY_ZONES

const selectOptimizerRegion = (s: any): string => s.optimizer?.region ?? ''

/**
 * Build a set of zone ids that belong to the given region.
 * Used as an intermediate selector so createSelector can memoize it.
 */
const selectZoneIdsForOptimizerRegion = createSelector(
  [selectOptimizerDistributionZones, selectOptimizerRegion],
  (zones, region): Set<string> => {
    if (!region) return new Set<string>()
    const result = new Set<string>()
    zones.forEach((z) => {
      if (z.region === region) result.add(z.id)
    })
    return result
  },
)

/**
 * All drivers that belong to the selected optimizer region, mapped to
 * DriverOption shape. A driver qualifies when:
 *   - isAllDistributionZones === true, OR
 *   - at least one of its zone ids is in the region's zone set.
 *
 * When the optimizer region is not yet set (empty string) all drivers are
 * returned (safe fallback matching the old behaviour).
 */
export const selectRegionDrivers = createSelector(
  [selectAllDispatchDrivers, selectZoneIdsForOptimizerRegion, selectOptimizerRegion],
  (rawDrivers, zoneIds, region): DriverOption[] => {
    const filtered = region
      ? rawDrivers.filter((d: any) => {
          if (d?.value?.isAllDistributionZones === true) return true
          const zones: string[] = d?.value?.zones ?? []
          return zones.some((zId) => zoneIds.has(zId))
        })
      : rawDrivers

    return filtered.map((d: any) => {
      const label: string = d?.label ?? ''
      const name = label.split('***')[0] ?? label
      // Name parts: prefer the source fields, fall back to splitting the label.
      const [fnFromLabel, ...restName] = name.trim().split(' ')
      return {
        title: name,
        desc: '',
        value: label,            // "Name***username" — matches existing usage
        data: {
          // The USERS doc id — the id getRoutes/getFleets report as driver.id and the
          // only one a USERS write can key on. fetchDispatchData puts the
          // driverAppUserName in `value.id` and the doc id in `firestoreDocId`, while
          // getRoutes' own driver options already use the doc id as `data.id`; both
          // lists get merged into the same pickers, so normalise here.
          id: d?.value?.firestoreDocId || d?.value?.id || '',
          firestoreDocId: d?.value?.firestoreDocId || d?.value?.id || '',
          active: true,
          distributionZones: d?.value?.zones ?? [],
          firstName: d?.value?.firstName || fnFromLabel || '',
          lastName: d?.value?.lastName || restName.join(' ') || '',
          email: d?.value?.email || '',
        },
      }
    })
  },
)

// ─── Dashboard (Today) region-driver selector ─────────────────────────────────
//
// Today mode uses s.dashboard.region (same id convention) but does NOT load
// distributionZones into its own slice. We reuse s.optimizer.distributionZones
// because the zone-→region map is project-global and the optimizer page typically
// loads first.  If it happens to be empty (Today opened before optimizer, fresh
// session) the filter degrades to returning all drivers — safe, never crashes.

const selectDashboardRegion = (s: any): string => s.dashboard?.region ?? ''

const selectZoneIdsForDashboardRegion = createSelector(
  [selectOptimizerDistributionZones, selectDashboardRegion],
  (zones, region): Set<string> => {
    if (!region) return new Set<string>()
    const result = new Set<string>()
    zones.forEach((z) => {
      if (z.region === region) result.add(z.id)
    })
    return result
  },
)

/**
 * All drivers in the currently selected dashboard (Today) region, mapped to
 * the optimizer DriverOption shape. Degrades to all drivers when the zone map
 * is not yet populated (s.optimizer.distributionZones is empty).
 */
export const selectRegionDriversDashboard = createSelector(
  [selectAllDispatchDrivers, selectZoneIdsForDashboardRegion, selectDashboardRegion, selectOptimizerDistributionZones],
  (rawDrivers, zoneIds, region, zones): DriverOption[] => {
    const zonesLoaded = zones.length > 0
    const filtered = (region && zonesLoaded)
      ? rawDrivers.filter((d: any) => {
          if (d?.value?.isAllDistributionZones === true) return true
          const dZones: string[] = d?.value?.zones ?? []
          return dZones.some((zId) => zoneIds.has(zId))
        })
      : rawDrivers

    return filtered.map((d: any) => {
      const label: string = d?.label ?? ''
      const name = label.split('***')[0] ?? label
      return {
        title: name,
        desc: '',
        value: label,
        data: {
          id: d?.value?.id ?? '',
          active: true,
          distributionZones: d?.value?.zones ?? [],
        },
      }
    })
  },
)
